import { Button } from "components/Button/Button";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { FeConfigType, SchemaType } from "config/config";
import { schemaSelector } from "containers/Resources/Resources.selectors";
import {
    columns,
    recordListQuery,
} from "containers/Resources/screens/ResourcesSingle/components/UsedByList/utils";
import { initialQuery } from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { getSearchObj } from "utils/utils";
import styles from "./UsedByList.module.scss";

interface UsedByListProps {
    resourceName: FeConfigType;
    id: string;
    usedByRecordType?: string;
    queryParams?: {
        query: string;
        params?: { id: string };
    };
    useFilter?: boolean;
    showSingleValueAsList?: boolean;
    referencedRecord?: FeConfigType;
    buttonProps?: {
        ButtonComponent?: () => any;
        label?: string;
        onClick?: () => void;
        isDisabled?: boolean;
        icon?: any;
        docs?: {
            title?: string;
            url: string;
        };
    };
}

export const UsedBySingleRecordList = ({
    id,
    resourceName,
    usedByRecordType = "",
    queryParams,
    useFilter = true,
    showSingleValueAsList,
    referencedRecord,
    buttonProps,
}: UsedByListProps) => {
    const dispatch = useDispatch();
    const schema: SchemaType[] = useSelector(schemaSelector);
    const referencedRecordName = useMemo(() => {
        if (usedByRecordType) {
            return;
        }
        const entry = schema.find((field) => resourceName === field.name);
        if (entry) {
            const referencedByForeignKeysArr = entry.fields.find(
                (field) => field.name === "id"
            )?.referenced_by_foreign_keys;
            const referencedRecordName =
                referencedRecord ??
                Object.values(referencedByForeignKeysArr ?? [])
                    .filter((reference) => reference.column === `${resourceName}_id`)
                    .map((reference) => reference.table)[0];
            return referencedRecordName;
        } else {
            return "";
        }
    }, [referencedRecord, resourceName, schema, usedByRecordType]);

    const key = `usedByList-${id}`;

    // localStorage tableSettings key, so page size / sort order persist (parity
    // with other record lists). UsedByList renders in MANY contexts (different
    // owning record types x different referenced/used-by record types), so a
    // single shared key would make every used-by panel collide on one persisted
    // page size. Scope the key per (owning resource, referenced record type) to
    // keep each panel independent. Server-side pagination + sortable columns ->
    // persist page size and sort order.
    const tableSettingsKey = useMemo(
        () =>
            `usedByList-${resourceName}-${
                referencedRecordName || usedByRecordType || "all"
            }`,
        [resourceName, referencedRecordName, usedByRecordType]
    );

    const [isLoading, setLoading] = useState(false);

    const usedByListSelector = (state) =>
        get(
            state?.dataStore?.[key],
            `${referencedRecordName ? `${referencedRecordName}_list` : `${usedByRecordType}`}`
        );
    const usedByData = useSelector(usedByListSelector);

    const usedByList = showSingleValueAsList
        ? usedByData
            ? [usedByData]
            : []
        : usedByData?.[`${referencedRecordName}_list`] ?? usedByData;
    const usedByListCount = showSingleValueAsList
        ? usedByData
            ? 1
            : 0
        : usedByData?.[`${referencedRecordName}_count`] ?? usedByData?.length;

    const [queryInfo, setQueryInfo] = useState(() =>
        getInitialQueryWithTableSettings(tableSettingsKey, initialQuery)
    );

    const updateQuery = useCallback(
        (newQuery: any) => {
            setQueryInfo({ ...queryInfo, ...newQuery });
        },
        [queryInfo]
    );

    const filterStr = useMemo(() => {
        const filter = useFilter
            ? getSearchObj({ field: `${resourceName}_id`, op: "eq", value: id })
            : null;
        return filter ? JSON.stringify(filter) : null;
    }, [id, resourceName, useFilter]);

    const getData = useCallback(() => {
        setLoading(true);
        if (referencedRecordName === "custom_object") {
            new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    type: dataStoreAction.START_FETCH_DATA,
                    key: `${id}-custom_object.referencing_custom_objects`,
                    params: {
                        query: queryParams?.query,
                        variables: { ...queryInfo, filter: filterStr, ...queryParams?.params },
                        allow_deleted: true,
                    },
                });
            })
                .then((data: any) => {
                    const idList = data?.custom_object?.referencing_custom_objects?.map(
                        (item) => item.id
                    );
                    if (idList) {
                        const idFilter = getSearchObj({ field: "id", op: "in", value: idList });
                        new Promise((resolve, reject) => {
                            dispatch({
                                promise: { resolve, reject },
                                type: dataStoreAction.START_FETCH_DATA,
                                key,
                                params: {
                                    query: recordListQuery("custom_object"),
                                    variables: {
                                        ...queryInfo,
                                        filter: JSON.stringify(idFilter),
                                        ...queryParams?.params,
                                    },
                                    allow_deleted: true,
                                },
                            });
                        });
                    }
                })
                .catch((error) => error)
                .finally(() => setLoading(false));
        } else {
            new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    type: dataStoreAction.START_FETCH_DATA,
                    key,
                    params: {
                        query: queryParams?.query ?? recordListQuery(referencedRecordName),
                        variables: { ...queryInfo, filter: filterStr, ...queryParams?.params },
                        allow_deleted: true,
                    },
                });
            })
                .catch((error) => error)
                .finally(() => setLoading(false));
        }
    }, [
        dispatch,
        filterStr,
        id,
        key,
        queryInfo,
        queryParams?.params,
        queryParams?.query,
        referencedRecordName,
    ]);

    useEffect(() => {
        if (referencedRecordName ?? usedByRecordType) {
            getData();
        }
    }, [getData, referencedRecordName, usedByRecordType]);

    return (
        <div className={styles.usedByFormContainer}>
            {buttonProps ? (
                <div className={styles.actionContainer}>
                    {buttonProps?.ButtonComponent ? (
                        <buttonProps.ButtonComponent />
                    ) : (
                        <Button
                            label={buttonProps.label}
                            onClick={buttonProps.onClick}
                            disabled={buttonProps.isDisabled}
                            variation="dark"
                            icon={buttonProps.icon}
                        />
                    )}
                    {buttonProps.docs ? (
                        <DocsInfoTooltip
                            title={buttonProps.docs.title}
                            urlPath={buttonProps.docs.url}
                        />
                    ) : null}
                </div>
            ) : null}
            <RecordTable
                columns={columns(resourceName === "setting" ? "execution" : referencedRecord)}
                data={usedByList}
                totalItemCount={usedByListCount}
                isLoading={isLoading}
                updateQuery={updateQuery}
                options={{
                    // "controls" = right-side display controls (column selector)
                    // without the left bulk-action chrome; this panel is
                    // selectableRows:"none" so bulk actions would be stray.
                    toolbar: "controls",
                    toolbarFilters: false,
                    showSearch: false,
                    viewColumns: true,
                    showDensityToggle: false,
                    selectableRows: "none",
                }}
                initialQuery={getInitialQueryWithTableSettings(
                    tableSettingsKey,
                    initialQuery
                )}
                tableSettingsKey={tableSettingsKey}
                recordTypes="all"
                refresh={getData}
            />
        </div>
    );
};
